import { useState, useEffect } from 'react';
import { AttendanceSheetStatusEnum } from '../../enums/attendance-sheet-status.enum';
import { sendMassNotification, readByIdAttendanceSheet } from '../../attendance-sheet.service';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import { AttendanceSheetNotificationDto, ListAllIntershipsOfTheMonthResponseDto } from '@/api.types';

interface MassNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  registros: ListAllIntershipsOfTheMonthResponseDto[];
}

interface FolhaComNotificacoes {
  id: string;
  notificacoes: AttendanceSheetNotificationDto[];
}

export function MassNotificationModal({ isOpen, onClose, registros }: MassNotificationModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [enviados, setEnviados] = useState<string[]>([]);
  const [erros, setErros] = useState<{ id: string, mensagem: string }[]>([]);
  const [notificacoesCompletas, setNotificacoesCompletas] = useState<FolhaComNotificacoes[]>([]);
  const [carregandoNotificacoes, setCarregandoNotificacoes] = useState(false);
  const [progressoEnvio, setProgressoEnvio] = useState(0);
  const [totalParaEnviar, setTotalParaEnviar] = useState(0);

  useEffect(() => {
    if (isOpen && registros.length > 0) {
      buscarNotificacoesCompletas();
    }
  }, [isOpen, registros]);

  const buscarNotificacoesCompletas = async () => {
    setCarregandoNotificacoes(true);
    try {
      const promises = registros.filter(a => a.folha?.id).map(async (registro) => {
        try {
          const response = await readByIdAttendanceSheet(registro.folha.id);

          return {
            id: registro.folha.id,
            notificacoes: response.notificacoes || []
          };
        } catch (error) {
          return {
            id: registro.folha.id,
            notificacoes: []
          };
        }
      });

      const resultados = await Promise.all(promises);
      setNotificacoesCompletas(resultados);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setCarregandoNotificacoes(false);
    }
  };

  const getNotificacoesPorFolha = (folhaId: string) => {
    const folha = notificacoesCompletas.find(item => item.id === folhaId);
    return folha ? folha.notificacoes : [];
  };

  const handleCheckboxChange = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const folhasNotificaveis = registros.filter(reg =>
        reg.folha.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ||
        reg.folha.status === AttendanceSheetStatusEnum.EM_CORRECAO
      ).map(reg => reg.folha.id);

      setSelectedIds(folhasNotificaveis);
    } else {
      setSelectedIds([]);
    }
  };

  const sendNotifications = async () => {
    if (selectedIds.length === 0) return;

    setIsSending(true);
    setEnviados([]);
    setErros([]);
    setProgressoEnvio(0);
    setTotalParaEnviar(selectedIds.length);

    try {
      // Função modificada para mostrar progresso
      const result = await sendMassNotification(selectedIds, (progresso) => {
        setProgressoEnvio(progresso);
      });

      // Atualiza os estados com os resultados
      setEnviados(result.success.map(s => s.split(':')[0].replace('Folha ', '').trim()));

      const novosErros = result.errors.map(error => {
        const match = error.match(/Folha (.+?):/);
        return {
          id: match ? match[1] : 'unknown',
          mensagem: error
        };
      });
      setErros(novosErros);

      await buscarNotificacoesCompletas();

      if (result.success.length > 0) {
        toast.success(`${result.success.length} notificação(ões) enviada(s) com sucesso`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} notificação(ões) falharam`);
      }

    } catch (error) {
      toast.error('Erro ao processar notificações em massa');
    } finally {
      setIsSending(false);
      setProgressoEnvio(0);
      setSelectedIds([]);
    }
  };

  const registrosNotificaveis = registros.filter(reg =>
    reg.folha?.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ||
    reg.folha?.status === AttendanceSheetStatusEnum.EM_CORRECAO
  );

  const porcentagemProgresso = totalParaEnviar > 0
    ? Math.round((progressoEnvio / totalParaEnviar) * 100)
    : 0;

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Notificação em Massa</Modal.Title>
      </Modal.Header>

      {isSending && (
        <div className="px-3 pt-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted">
              Enviando notificações... {progressoEnvio} de {totalParaEnviar}
            </small>
            <small className="text-muted">{porcentagemProgresso}%</small>
          </div>
          <ProgressBar
            now={porcentagemProgresso}
            animated
            striped
            variant="primary"
            style={{ height: '8px' }}
          />
        </div>
      )}

      <Modal.Body>
        {(enviados.length > 0 || erros.length > 0) && (
          <div className="alert alert-info mb-3">
            <strong>Resultado do envio:</strong>
            {enviados.length > 0 && <span className="text-success ms-2">✅ {enviados.length} enviadas</span>}
            {erros.length > 0 && <span className="text-danger ms-2">❌ {erros.length} falhas</span>}
          </div>
        )}

        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="selectAll"
            onChange={(e) => handleSelectAll(e.target.checked)}
            checked={selectedIds.length === registrosNotificaveis.length && registrosNotificaveis.length > 0}
            disabled={registrosNotificaveis.length === 0 || isSending || carregandoNotificacoes}
          />
          <label className="form-check-label" htmlFor="selectAll">
            Selecionar Todos ({registrosNotificaveis.length} notificáveis)
          </label>
        </div>

        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Selecionar</th>
                <th>Nome</th>
                <th>Notificações</th>
                <th>Última Notificação</th>
                <th>Status</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {registros
                .filter(a => a.folha?.id)
                .filter((a, i, self) => i === self.findIndex(r => r.folha?.id === a.folha?.id))
                .map((registro) => {
                  const podeNotificar =
                    registro.folha.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ||
                    registro.folha.status === AttendanceSheetStatusEnum.EM_CORRECAO;

                  const foiEnviado = enviados.includes(registro.folha.id);
                  const erro = erros.find(e => e.id === registro.folha.id);
                  const notificacoes = getNotificacoesPorFolha(registro.folha.id);
                  const ultimaNotificacao = notificacoes.length > 0
                    ? notificacoes[notificacoes.length - 1]
                    : null;

                  return (
                    <tr key={registro.folha.id} className={!podeNotificar ? 'text-muted' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(registro.folha.id)}
                          onChange={(e) => handleCheckboxChange(registro.folha.id, e.target.checked)}
                          disabled={!podeNotificar || isSending || carregandoNotificacoes}
                        />
                      </td>
                      <td>{registro.estagiario.nome}</td>
                      <td>
                        <span className="badge bg-info">
                          {notificacoes.length}
                        </span>
                      </td>
                      <td>
                        {ultimaNotificacao
                          ? format(new Date(ultimaNotificacao.criadoEm), 'dd/MM/yyyy HH:mm')
                          : 'Nunca'
                        }
                      </td>
                      <td>
                        <span className={`badge ${registro.folha?.status === AttendanceSheetStatusEnum.EM_PREENCHIMENTO ? 'bg-warning' :
                          registro.folha.status === AttendanceSheetStatusEnum.EM_CORRECAO ? 'bg-danger' :
                            registro.folha.status === AttendanceSheetStatusEnum.AGUARDANDO_ASSINATURA ? 'bg-info' :
                              registro.folha.status === AttendanceSheetStatusEnum.CONCLUIDA ? 'bg-success' : 'bg-secondary'
                          }`}>
                          {registro.folha.status}
                        </span>
                        {!podeNotificar && <small className="ms-1">(Não notificável)</small>}
                      </td>
                      <td>
                        {foiEnviado && <span className="text-success">✅ Enviado</span>}
                        {erro && <span className="text-danger" title={erro.mensagem}>❌ Erro</span>}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={sendNotifications}
          disabled={selectedIds.length === 0 || isSending || carregandoNotificacoes}
        >
          {isSending ? 'Enviando...' : `Notificar (${selectedIds.length})`}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSending}
        >
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}